import React from 'react';
import { useFadeIn } from '../hooks/useFadeIn';
import './ProfileDemoSection.css';

interface ProfileAnalysis {
  faceDetected: boolean;
  aiGenerated: boolean;
  overallScore: number;
  trustScore: number;
}

interface ProfileData {
  name: string;
  age: number;
  bio: string;
  imageUrl: string;
  label: 'Real' | 'Fake';
  analysis: ProfileAnalysis;
}

interface ProfileDemoSectionProps {
  onLoginClick?: () => void;
}

const ProfileDemoSection: React.FC<ProfileDemoSectionProps> = ({ onLoginClick }) => {
  const headerFadeIn = useFadeIn({ delay: 0.1 });
  const profilesFadeIn = useFadeIn({ delay: 0.2 });
  const ctaFadeIn = useFadeIn({ delay: 0.3 });

  // Mock profile data
  const profiles: ProfileData[] = [
    {
      name: 'Sarah',
      age: 28,
      bio: 'Puppy enthusiast. Ice cream connoisseur. A seeker of great coffee dates.',
      imageUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=600&fit=crop',
      label: 'Real',
      analysis: {
        faceDetected: true,
        aiGenerated: false,
        overallScore: 92,
        trustScore: 95
      }
    },
    {
      name: 'Alex',
      age: 26,
      bio: 'Looking for love please contact me.',
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop',
      label: 'Fake',
      analysis: {
        faceDetected: true,
        aiGenerated: true,
        overallScore: 34,
        trustScore: 28
      }
    }
  ];

  const getScoreColor = (score: number) => {
    if (score >= 70) return '#6366f1'; // indigo
    if (score >= 40) return '#667eea'; // purple from gradient
    return '#b91c5c'; // dimmer pink/crimson
  };

  return (
    <section className="profile-demo-section landing-section">
      <div className="demo-container landing-container">
        <div
          ref={headerFadeIn.ref}
          className={`demo-header ${headerFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${headerFadeIn.delay}s` } as React.CSSProperties}
        >
          <h2 className="demo-title">Review user risk faster with context your team can trust.</h2>
          <p className="demo-subtitle">AI-powered risk analysis for faster, more confident moderation decisions.</p>
        </div>

        <div
          ref={profilesFadeIn.ref}
          className={`profiles-grid ${profilesFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${profilesFadeIn.delay}s` } as React.CSSProperties}
        >
          {profiles.map((profile, index) => (
            <article key={index} className="profile-card">
              <div className="profile-image-shell">
                <img
                  src={profile.imageUrl}
                  alt={profile.name}
                  className="profile-image"
                  loading="lazy"
                />
                <div className="image-gradient-overlay"></div>
                <div className="profile-info-overlay">
                  <h3 className="profile-name">{profile.name}, {profile.age}</h3>
                  <p className="profile-bio">{profile.bio}</p>
                  <span className={`profile-tag ${profile.label === 'Real' ? 'is-real' : 'is-fake'}`}>{profile.label}</span>
                </div>
              </div>
              <div
                className="analysis-panel"
                style={{ '--score-color': getScoreColor(profile.analysis.overallScore) } as React.CSSProperties}
              >
                <div className="analysis-panel-top">
                  <p>Analysis score</p>
                  <strong>{profile.analysis.overallScore}</strong>
                </div>
                <div className="analysis-row">
                  <span>Face detected</span>
                  <b>{profile.analysis.faceDetected ? 'Yes' : 'No'}</b>
                </div>
                <div className="analysis-row">
                  <span>AI-generated</span>
                  <b>{profile.analysis.aiGenerated ? 'Yes' : 'No'}</b>
                </div>
                <div className="analysis-row">
                  <span>Bio trust score</span>
                  <b>{profile.analysis.trustScore}</b>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div
          ref={ctaFadeIn.ref}
          className={`demo-cta ${ctaFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${ctaFadeIn.delay}s` } as React.CSSProperties}
        >
          <button className="landing-cta-primary" onClick={onLoginClick}>
            Open Moderation Dashboard
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProfileDemoSection;

